import React, { FC, useEffect, useRef, useState } from 'react';

import { setFocusOnExtent, setMapMode } from 'store/mapSlice';

import { useAppDispatch } from 'hooks/defaultStoreHooks';
import { useDialog } from 'hooks/useDialog';
import { useMapInteraction } from 'hooks/useMapInteraction';

import { RaaDialog } from 'raa-react';

import { CenterActiveSourceGeometryButton } from 'components/CenterActiveSourceGeometryButton/CenterActiveSourceGeometryButton';
import { DrawerSection } from 'components/drawerSections/DrawerSections';
import RaaInputField from 'components/fieldComponents/raaInputField/RaaInputField';
import GeometryFileDownload from 'components/geometryFileDownload/GeometryFileDownload';
import { GeometryFileUploadInput } from 'components/geometryFileUpload/GeometryFileUpload';
import { activeSource, replaceSingleUtkastFeaturesInActiveSource } from 'components/map/Layers';
import { TextButton } from 'components/sharedElements/sharedElements';
import TagList from 'components/tagList/TagList';
import CategoriesWithTagList from 'components/utkast/categoriesWithTagList/CategoriesWithTagList';
import { EnskiltFormValues } from 'components/utkast/utkastRegistration/UtkastFormSchema';

import {
  Alternativnamn,
  AndamalPut,
  AndamalTag,
  BebyggelseobjektDomaner,
  BebyggelseobjektUtkast,
  HuvudgruppTyp,
  HuvudkategoriTyp,
  UnderkategoriTyp
} from 'models';

import { BebyggelseobjektgruppEnum, MapModeEnum } from 'enums';

import { Feature } from 'ol';
import GeoJSON, { GeoJSONFeatureCollection } from 'ol/format/GeoJSON';

import { useFormikContext } from 'formik';
import { andamalTagToAndamalPut, convertFromAndamalToAndamalTag } from 'utilities/convertUtkastTypes';
import { hasSelfIntersections } from 'utilities/hasSelfIntersections';
import { v4 as uuid } from 'uuid';

interface GrundinformationProps {
  utkastData: BebyggelseobjektUtkast;
  domaner: BebyggelseobjektDomaner;
  primarKategoriLabel: string;
  sekundarKategoriLabel: string;
}

const GrundinformationSection: FC<GrundinformationProps> = ({
  utkastData,
  domaner,
  primarKategoriLabel,
  sekundarKategoriLabel
}) => {
  const { setFieldValue, setFieldTouched, touched, errors, values } = useFormikContext<EnskiltFormValues>();
  const { setDialog, unsetDialog } = useDialog();
  const dispatch = useAppDispatch();

  const [alternativnamnText, setAlternativnamnText] = useState<string>('');
  const [alternativNamnList, setAlternativnamnList] = useState<Array<Alternativnamn>>([]);
  const [alternativnamnTextActive, setAlternativnamnTextActive] = useState<boolean>(false);
  const [primaryAndamal, setPrimaryAndamal] = useState<Array<AndamalTag>>([]);
  const [secondaryAndamal, setSecondaryAndamal] = useState<Array<AndamalTag>>([]);
  const [secondaryCategorySelectActive, setSecondaryCategorySelectActive] = useState<boolean>(false);
  const [convertingFile, setConvertingFile] = useState<boolean>(false);
  const [filteredKategorier, setFilteredKategorier] = useState<Array<HuvudgruppTyp>>([]);
  const { setInteractionOptions, unsetInteractionOptions } = useMapInteraction();
  const fileUploadRef = useRef<HTMLInputElement>(null);

  const handleEditGeometry = (
    featureCollection: GeoJSONFeatureCollection,
    setFieldTouched: (field: string, isTouched?: boolean, shouldValidate?: boolean) => void,
    setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void
  ) => {
    setInteractionOptions({
      geometry: featureCollection,
      onProceed: (featureCollection: GeoJSONFeatureCollection) =>
        handleInteractionProceed(featureCollection, setFieldTouched, setFieldValue),
      onProceedGuard: (featureCollection: GeoJSONFeatureCollection) => {
        const geoJson = new GeoJSON();

        const blockedBySelfIntersections = hasSelfIntersections(featureCollection, geoJson);

        return {
          blocked: blockedBySelfIntersections,
          header: 'Området innehåller en självkorsande geometri',
          message: 'Ta bort korsningen för att kunna spara det nya området.'
        };
      }
    });
  };

  const handleInteractionProceed = (
    featureCollection: GeoJSONFeatureCollection,
    setFieldTouched: (field: string, isTouched?: boolean, shouldValidate?: boolean) => void,
    setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void
  ) => {
    if (featureCollection) {
      setFieldTouched('geometri');
      setFieldValue('geometri', featureCollection);
      replaceSingleUtkastFeaturesInActiveSource(featureCollection, utkastData);
      dispatch(
        setFocusOnExtent({
          type: 'fit',
          extent: activeSource.getExtent()
        })
      );
    }

    dispatch(setMapMode(MapModeEnum.UTKAST_REGISTRATION));
    unsetInteractionOptions();
  };

  const getGeometryName = (geometryTouched: boolean | undefined): string => {
    if (utkastData.namn && geometryTouched) {
      return `${utkastData.namn} ${new Date().toLocaleDateString('sv-SE')}`;
    } else {
      return `${utkastData.titel} ${new Date().toLocaleDateString('sv-SE')}`;
    }
  };

  const addAlternativnamn = async () => {
    const altNamnId = uuid();
    await setFieldValue('alternativnamn', [
      ...alternativNamnList,
      { attribut_id: altNamnId, namn: alternativnamnText }
    ]);
    setAlternativnamnList((current) => [...current, { attribut_id: altNamnId, namn: alternativnamnText }]);
    setAlternativnamnText('');
  };

  const onClickRemoveAndamalTag = (removedItem: AndamalTag, onProceedRemoval: () => void) => {
    if (!removedItem.aktiv) {
      setDialog(RaaDialog, {
        header: 'Ta bort utfasat värde?',
        children: (
          <p id="dialog-description" className="tw-my-4">
            Du kommer inte att kunna välja den här kategorin igen.
          </p>
        ),
        cancel: () => unsetDialog(),
        cancelButtonText: 'Avbryt',
        proceedButtonText: 'Ta bort',
        proceed: () => {
          onProceedRemoval();
          unsetDialog();
        },
        ariaDescribedBy: 'dialog-description'
      });
    } else {
      onProceedRemoval();
    }
  };

  const removePrimaryAndamal = () => {
    setPrimaryAndamal([]);
    void setFieldValue('ursprungligt_primar_andamal', null);
  };

  const removeFromSecondaryAndamal = async (removedItem: AndamalTag) => {
    const filteredSekundarAndamal = secondaryAndamal.filter((item: AndamalTag) => removedItem !== item);
    const ursprungligtSekundarAndamal: AndamalPut[] = filteredSekundarAndamal.map(andamalTagToAndamalPut);
    await setFieldValue('ursprungligt_sekundar_andamal', ursprungligtSekundarAndamal);
    setSecondaryAndamal(filteredSekundarAndamal);
  };

  useEffect(() => {
    if (utkastData && domaner) {
      if (domaner?.kategorityper) {
        setFilteredKategorier(
          domaner.kategorityper
            .filter((huvudgrupp: HuvudgruppTyp) => huvudgrupp.aktiv)
            .map((huvudgrupp: HuvudgruppTyp) => ({
              ...huvudgrupp,
              huvudkategorier: huvudgrupp.huvudkategorier
                .filter((huvudkategori: HuvudkategoriTyp) => huvudkategori.aktiv)
                .map((huvudkategori: HuvudkategoriTyp) => ({
                  ...huvudkategori,
                  underkategorier: huvudkategori.underkategorier.filter(
                    (underkategori: UnderkategoriTyp) => underkategori.aktiv
                  )
                }))
            }))
        );

        if (utkastData?.ursprungligt_sekundar_andamal) {
          const res = convertFromAndamalToAndamalTag(utkastData.ursprungligt_sekundar_andamal, domaner.kategorityper);
          setSecondaryAndamal(res as AndamalTag[]);
        }

        if (utkastData?.ursprungligt_primar_andamal) {
          const res = convertFromAndamalToAndamalTag([utkastData.ursprungligt_primar_andamal], domaner.kategorityper);
          setPrimaryAndamal(res);
        }
      }

      if (utkastData?.alternativnamn) {
        setAlternativnamnList(utkastData.alternativnamn);
        if (utkastData?.alternativnamn.length > 0) {
          setAlternativnamnTextActive(true);
        }
      }
    }
  }, [utkastData, domaner]);

  return (
    <DrawerSection>
      <fieldset>
        <legend className="tw-heading-md tw-mb-4">Grundinformation</legend>

        <div className="tw-mb-2">
          <label className="raa-label" htmlFor="namn">
            Objektets namn
          </label>
          {utkastData?.lm_registerbyggnad?.byggnadnamn && utkastData?.lm_registerbyggnad.byggnadnamn.length > 0 && (
            <TextButton
              icon="raa-icon-cut"
              onClick={() => setFieldValue('namn', utkastData.lm_registerbyggnad?.byggnadnamn[0])}
            >
              Klistra in Lantmäteriets namn ({utkastData.lm_registerbyggnad?.byggnadnamn[0]})
            </TextButton>
          )}
          <RaaInputField name="namn" placeholder="Ange ett namn om det finns" shouldValidateForm={true} />
        </div>
        {utkastData.bebyggelseobjektgrupp === BebyggelseobjektgruppEnum.ENSKILT_BEBYGGELSEOBJEKT && (
          <div className="tw-my-2">
            {alternativNamnList.length === 0 && !alternativnamnTextActive && (
              <TextButton
                icon="raa-icon-add"
                disabled={values.namn.length === 0}
                onClick={() => setAlternativnamnTextActive(true)}
              >
                Lägg till alternativnamn
              </TextButton>
            )}

            {alternativnamnTextActive && (
              <>
                <label className="raa-label" htmlFor="addAlternativnamn">
                  Alternativnamn
                </label>
                <div className="tw-mb-2 tw-flex tw-gap-1 tw-whitespace-nowrap">
                  <input
                    name="addAlternativnamn"
                    id="addAlternativnamn"
                    type="text"
                    className="raa-input"
                    autoComplete="off"
                    maxLength={255}
                    value={alternativnamnText}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                      setAlternativnamnText(event.currentTarget.value)
                    }
                    onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) => {
                      if (event.key === 'Enter') {
                        event.preventDefault();
                        if (alternativNamnList.length < 3 && alternativnamnText.length > 0) {
                          void addAlternativnamn();
                        }
                      }
                    }}
                    disabled={alternativNamnList.length > 2}
                    placeholder="Ange ett namn"
                  />
                  <button
                    type="button"
                    className="raa-button raa-button-secondary"
                    disabled={alternativNamnList.length > 2 || alternativnamnText.length === 0}
                    onClick={() => {
                      void addAlternativnamn();
                    }}
                  >
                    Lägg till
                  </button>
                </div>
              </>
            )}

            <TagList
              list={alternativNamnList}
              leftColumn={(item: Alternativnamn) => item.namn}
              onRemoveItem={async (removedItem: Alternativnamn) => {
                const filteredItem = alternativNamnList.filter(
                  (item: Alternativnamn) => removedItem.attribut_id !== item.attribut_id
                );
                await setFieldValue('alternativnamn', filteredItem);
                setAlternativnamnList(filteredItem);
              }}
            />
          </div>
        )}

        <div className="tw-mb-5">
          <h2 className="raa-label">Geometri *</h2>

          <div className="tw-flex tw-justify-between">
            {convertingFile && <span className="tw-italic tw-text-raa-gray-5">Konverterar filen...</span>}
            {!convertingFile && (
              <div className="tw-flex">
                <TextButton
                  icon="raa-icon-edit"
                  onClick={() => handleEditGeometry(values.geometri, setFieldTouched, setFieldValue)}
                >
                  {values.geometri ? 'Ändra' : 'Rita yta'}
                </TextButton>

                <GeometryFileUploadInput
                  id="geometri"
                  converting={(converting: boolean) => setConvertingFile(converting)}
                  success={(features: Feature[]) => handleInteractionProceed(features, setFieldTouched, setFieldValue)}
                  labelText={values.geometri ? 'Ny från fil' : 'Från fil'}
                  showReplaceWarning={!!values.geometri}
                  fileUploadRef={fileUploadRef}
                />
              </div>
            )}
            <div className="tw-flex tw-gap-2">
              {values.geometri && !convertingFile && (
                <>
                  <CenterActiveSourceGeometryButton />
                  <GeometryFileDownload
                    fileName={getGeometryName(touched.geometri)}
                    geometry={values.geometri}
                  ></GeometryFileDownload>
                </>
              )}
            </div>
          </div>

          <p className="tw-mt-1 tw-text-raa-error-red">{errors.geometri && touched.geometri && errors.geometri}</p>
        </div>

        <CategoriesWithTagList
          categories={filteredKategorier}
          bebyggelseobjekgrupp={
            utkastData.bebyggelseobjektgrupp === BebyggelseobjektgruppEnum.SAMMANSATT
              ? BebyggelseobjektgruppEnum.OMRADE
              : utkastData.bebyggelseobjektgrupp
          }
          label={primarKategoriLabel}
          id="ursprungligt_primar_andamal"
          onRemoveTag={(removeItem: AndamalTag) => {
            onClickRemoveAndamalTag(removeItem, () => removePrimaryAndamal());
          }}
          setTagList={(andamalTags: Array<AndamalTag>) => {
            setPrimaryAndamal((current) => [...current, ...andamalTags]);
            const ursprungligtPrimarAndamal: AndamalPut[] = [...primaryAndamal, ...andamalTags].map(
              andamalTagToAndamalPut
            );
            void setFieldValue('ursprungligt_primar_andamal', ursprungligtPrimarAndamal[0]);
          }}
          tagList={primaryAndamal}
          categoriesSelectDisabled={primaryAndamal.length > 0}
          hasError={Boolean(errors.ursprungligt_primar_andamal)}
        />
        <p hidden={Boolean(!errors.ursprungligt_primar_andamal)} className="tw-mt-1 tw-text-raa-error-red">
          {errors.ursprungligt_primar_andamal}
        </p>

        <div className="tw-my-2">
          {!secondaryCategorySelectActive && secondaryAndamal.length === 0 ? (
            <TextButton icon="raa-icon-add" onClick={() => setSecondaryCategorySelectActive(true)}>
              {sekundarKategoriLabel}
            </TextButton>
          ) : (
            <>
              <CategoriesWithTagList
                categories={filteredKategorier}
                disabledList={primaryAndamal}
                bebyggelseobjekgrupp={utkastData.bebyggelseobjektgrupp}
                label={sekundarKategoriLabel}
                id="ursprungligt_sekundar_andamal"
                onRemoveTag={(item: AndamalTag) => {
                  onClickRemoveAndamalTag(item, () => removeFromSecondaryAndamal(item));
                }}
                setTagList={(andamalTags: Array<AndamalTag>) => {
                  setSecondaryAndamal((current) => [...current, ...andamalTags]);
                  const ursprungligtSekundarAndamal: AndamalPut[] = [...secondaryAndamal, ...andamalTags].map(
                    andamalTagToAndamalPut
                  );
                  void setFieldValue('ursprungligt_sekundar_andamal', ursprungligtSekundarAndamal);
                }}
                tagList={secondaryAndamal}
                hasError={Boolean(errors.ursprungligt_sekundar_andamal)}
              />

              <p hidden={Boolean(!errors.ursprungligt_sekundar_andamal)} className="tw-mt-1 tw-text-raa-error-red">
                {errors.ursprungligt_sekundar_andamal?.toString()}
              </p>
            </>
          )}
        </div>
      </fieldset>
    </DrawerSection>
  );
};

export default GrundinformationSection;
